import {useState, useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {Table, Typography, Select, Tag, Tooltip} from 'antd';
import moment from 'moment';
import {GET_WAIT_LIST_DATA, GET_ALL_COUNTRIES} from 'graphql/queries/users';
import {
  HAS_ACCESSED_APP_OPTIONS,
  getStatusColor,
  copyToClipboard,
  formatAccessInfo,
} from './waitListUtils';
import styles from './usersWaitList.module.scss';

const {Title, Text} = Typography;
const {Option} = Select;

export const UsersWaitList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    hasAccessedApp: '',
    country: '',
  });

  // Fetch countries for dropdown
  const {data: countriesData} = useQuery(GET_ALL_COUNTRIES, {
    errorPolicy: 'ignore',
  });

  // Extract countries from the response (filter out Error types)
  const countries =
    countriesData?.getAllCountries?.filter(
      country => country?.__typename !== 'Error',
    ) || [];

  // Fetch waitlist data
  const {data, loading, refetch} = useQuery(GET_WAIT_LIST_DATA, {
    variables: {
      filters: {
        page: currentPage,
        limit,
        ...(filters.hasAccessedApp && {
          hasAccessedApp: filters.hasAccessedApp === 'true',
        }),
        ...(filters.country && {country: filters.country}),
      },
    },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const waitlistResponse =
    data?.getWaitlistEntries?.__typename === 'ResponseWithWaitlistEntries'
      ? data?.getWaitlistEntries
      : null;

  const waitlistData = waitlistResponse?.data || [];
  const pagination = waitlistResponse?.pagination || {};

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
  };

  useEffect(() => {
    refetch({
      filters: {
        page: currentPage,
        limit,
        ...(filters.hasAccessedApp && {
          hasAccessedApp: filters.hasAccessedApp === 'true',
        }),
        ...(filters.country && {country: filters.country}),
      },
    });
  }, [currentPage, limit]);

  const handleFilterChange = (key, value) => {
    const newFilters = {...filters, [key]: value};
    setFilters(newFilters);
    setCurrentPage(1);
    refetch({
      filters: {
        page: 1,
        limit,
        ...(newFilters.hasAccessedApp && {
          hasAccessedApp: newFilters.hasAccessedApp === 'true',
        }),
        ...(newFilters.country && {country: newFilters.country}),
      },
    });
  };

  const columns = [
    {
      title: 'Full Name',
      key: 'fullName',
      render: (_, record) => {
        const name =
          `${record?.user?.firstName || ''} ${
            record?.user?.lastName || ''
          }`.trim() || '-';
        return <span>{name}</span>;
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => record?.user?.email || '-',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: country => country || '-',
    },
    {
      title: 'Global Waitlist No.',
      key: 'globalWaitListNumber',
      render: (_, record) => record?.globalWaitListNumber || '-',
    },
    {
      title: 'Country Waitlist No.',
      key: 'countryWaitListNumber',
      render: (_, record) => record?.countryWaitListNumber || '-',
    },
    {
      title: 'Has Accessed App',
      key: 'hasAccessedApp',
      render: (_, record) => {
        const hasAccessed = record?.hasAccessedApp;
        return (
          <Tag color={getStatusColor(hasAccessed)}>
            {hasAccessed ? 'Yes' : 'No'}
          </Tag>
        );
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: ip => ip || '-',
    },
    {
      title: 'First Accessed From',
      key: 'firstAccessedFrom',
      render: (_, record) => {
        const firstAccess = record?.firstAccessedFrom;
        if (!firstAccess) return '-';
        const fullText = formatAccessInfo(firstAccess);
        return (
          <Tooltip title={fullText}>
            <span
              className={styles.truncatedText}
              onClick={() => copyToClipboard(fullText)}
              title="Click to copy">
              {fullText}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Last Accessed From',
      key: 'lastAccessedFrom',
      render: (_, record) => {
        const lastAccess = record?.lastAccessedFrom;
        if (!lastAccess) return '-';
        const fullText = formatAccessInfo(lastAccess);
        return (
          <Tooltip title={fullText}>
            <span
              className={styles.truncatedText}
              onClick={() => copyToClipboard(fullText)}
              title="Click to copy">
              {fullText}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created At',
      key: 'createdAt',
      render: (_, record) =>
        record?.createdAt
          ? moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss')
          : '-',
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2}>Users Wait list</Title>
        <Text type="secondary">View and manage users on the wait list</Text>
      </div>

      {/* Filters Section */}
      <div className={styles.filters}>
        <Select
          placeholder="Has Accessed App"
          className={styles.select}
          value={filters.hasAccessedApp || undefined}
          onChange={value => handleFilterChange('hasAccessedApp', value || '')}
          allowClear>
          {HAS_ACCESSED_APP_OPTIONS.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Select Country"
          className={styles.select}
          value={filters.country || undefined}
          onChange={value => handleFilterChange('country', value || '')}
          allowClear
          showSearch
          optionFilterProp="label"
          filterOption={true}>
          {countries.map(country => (
            <Option
              key={country?.countryCode}
              value={country?.countryCode}
              label={`${country?.name} (${country?.countryCode})`}>
              {country?.name} ({country?.countryCode})
            </Option>
          ))}
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={waitlistData}
        loading={loading}
        className="antd-table"
        pagination={{
          current: currentPage,
          pageSize: limit,
          showSizeChanger: true,
          showQuickJumper: true,
          total: pagination?.total || 0,
          onChange: handlePageChange,
        }}
        rowKey={(record, index) => record?.id || index}
      />
    </div>
  );
};
